import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {formatMoney} from '@shopify/theme-currency';

import storefrontRequestHeaders, {cartQuery} from '../../utils/storefrontAPIQueries';

import FreeShippingBar from './free-shipping-bar';
import Discount from './discount';
import GiftWithPurchase, {getGiftWithPurchase} from './gift-with-purchase';
import CartItem from './cart-item';
import CartTagalysRecommendations from './tagalys-recommendations';

import iconClose from '../../../assets/icon-close.svg';
import PromoBanner from './promo-banner/PromoBanner';

/* eslint-disable babel/camelcase */
const emptyCart = {
  item_count: 0,
  items: [],
  total_price: 0,
};
/* eslint-enable babel/camelcase */

const getItemsCount = (items = []) => {
  return items.reduce((total, item) => {
    if (item.product_type === 'Donation') return total;
    return total + item.quantity;
  }, 0);
};

const SideCart = ({rootUrl, freeShippingBarSettings}) => {
  const isCartPage = window.location.pathname === '/cart' || window.location.pathname === '/cart/';
  const defaultCart = window.theme.cart ? window.theme.cart : emptyCart;

  const [cart, setCart] = useState(defaultCart);
  const [discountData, setDiscountData] = useState({});
  const [customCartData, setCustomCartData] = useState({});
  const [errorMessage, setErrorMessage] = useState(null);

  // Get the default applied discounts from the cart level discount applications
  const defaultAppliedDiscounts = defaultCart.cart_level_discount_applications
    ?.filter((discount) => discount.type === 'discount_code')
    ?.map((discount) => discount.title)
    .join(',');
  const [appliedDiscounts, setAppliedDiscounts] = useState(defaultAppliedDiscounts);

  const itemsCount = getItemsCount(cart.items);
  const cartItems = cart.items
    /**
     * Add an isValidBundle boolean to the item if it belongs to a bundle
     * and there is a correct amount of paired items as stated in the _bundle_items
     * property.
     */
    ?.map((cartItem) => {
      if (!cartItem.properties || !cartItem?.properties?._bundle_id) return cartItem;

      /* eslint-disable babel/camelcase */
      const {_bundle_id, _bundle_items} = cartItem.properties;

      const pairedBundleItems = cart.items?.filter((_cartItem) => _cartItem?.properties?._bundle_id === _bundle_id);
      const isValidBundle = pairedBundleItems.length === _bundle_items;
      /* eslint-enable babel/camelcase */

      return {
        ...cartItem,
        isValidBundle,
      };
    });

  const giftWithPurchaseEnabled = window.theme.gwp?.enabled ?? false;
  const gwp = giftWithPurchaseEnabled ? getGiftWithPurchase(window.theme.gwp?.products, cartItems) : false;

  /**
   * GWP VALIDATION
   * --------------------------------------------------------
   * This function checks if:
   * - The projected qualifying spend is greater than the minimum spend for the gift.
   * - There are duplicate gifts in the cart.
   * - There are gifts in the cart that are not enabled in the theme editor.
   *
   * @param {object} updates - Object containing [line_item_key]:[quantity] pairs.
   */

  const getInvalidGifts = ({updates}) => {
    const invalidGifts = [];
    const giftsInCart = cartItems.filter((item) => item.properties?._gwp);

    const projectedCartTotal = () => {
      if (!updates || !Object.keys(updates).length) return total;

      const triggerLineItem = cart.items.find((item) => item.key === Object.keys(updates)[0]);
      if (!triggerLineItem) return total;

      const cartTotalExcludingGifts = cart.items.reduce((acc, item) => {
        return item.properties?._gwp ? acc : acc + item.final_price * item.quantity;
      }, 0);

      if (triggerLineItem.properties?._gwp) return cartTotalExcludingGifts;

      const quantityDifference = updates[Object.keys(updates)[0]] - triggerLineItem.quantity;
      const modifier = triggerLineItem.final_price * Math.abs(quantityDifference);

      if (quantityDifference > 0) return cartTotalExcludingGifts + modifier;
      return cartTotalExcludingGifts - modifier;
    };

    giftsInCart.forEach((gift) => {
      const giftSettings = window.theme.gwp.products.find((product) => product.product_id === gift.product_id);

      if (!giftSettings) {
        invalidGifts.push({[gift.key]: 0});
        return;
      }

      if (projectedCartTotal() < giftSettings.minimum_spend) {
        invalidGifts.push({[gift.key]: 0});
      } else if (gift.quantity > 1) {
        invalidGifts.push({[gift.key]: gift.quantity - 1});
      }
    });

    return invalidGifts;
  };

  const total = discountData?.totalPrice ?? cart.total_price;

  // Group line item discounts by title and sum their amounts
  const lineItemDiscountsGrouped = [];

  cart.items?.forEach((item) => {
    item.discounts?.forEach((discount) => {
      const existingDiscount = lineItemDiscountsGrouped.find((d) => d.title === discount.title);

      if (existingDiscount) {
        existingDiscount.amount += discount.amount;
      } else {
        lineItemDiscountsGrouped.push({...discount});
      }
    });
  });

  const cartItemsIDs = cartItems?.map((item) => item.product_id) ?? [];
  const [widgetProductIds, setWidgetProductIds] = useState(cartItemsIDs);
  const enableTagalysCartWidget = window.theme?.tagalys?.enableCartWidget;

  const cartEmptyText = window.theme.cartEmptyText ?? '';
  const cartyEmptyCTAs = window.theme.cartyEmptyCTAs ?? [];
  const cartTrustIndicators = window.theme.cartTrustIndicators ?? [];

  const updateCart = async (newCart) => {
    setCart(newCart);

    // Update the discount data
    await fetchDiscountData();

    // Check if there are any new items in the cart that aren't in the customCartData
    const newCartItemIDs = newCart.items?.map((item) => item.id);
    const hasNewItems = newCartItemIDs?.some(
      (id) => !customCartData.items?.some((customCartItem) => customCartItem.id === id),
    );
    const newProductIds = newCart.items?.map((item) => item.product_id) ?? [];
    const itemsChanged =
      widgetProductIds.length !== newProductIds.length || !widgetProductIds.every((id) => newProductIds.includes(id));

    if (hasNewItems) {
      // Fetch new custom cart data and update sessionStorage
      const newCustomCartData = await fetchCustomCartData();
      setCustomCartData(newCustomCartData);
      sessionStorage.setItem('cartCustomData', JSON.stringify(newCustomCartData));
    }

    // Update widgetProductIds when items change (added or removed)
    if (itemsChanged) {
      setWidgetProductIds(newProductIds);
    }

    const counters = document.querySelectorAll('[data-items-count]');
    const newCartCount = getItemsCount(newCart.items);
    counters.forEach((counter) => {
      counter.innerHTML = newCartCount;
      // Toggle the hide class based on the cart count
      if (newCartCount > 0) {
        counter.classList.remove('hide');
      } else {
        counter.classList.add('hide');
      }
    });
  };

  const fetchCart = async () => {
    const url = `${rootUrl}/cart.js`;
    const options = {
      headers: {
        'Content-Type': 'application/json',
      },
    };
    try {
      const response = await fetch(url, options);
      if (response.ok === false) {
        throw new Error('Fetch cart response was not OK');
      }
      const json = await response.json();
      return json;
    } catch (err) {
      window.console.error(err);
    }
  };

  const fetchDiscountData = async () => {
    // Create the URL and options for the API call
    const url = `https://${window.Shopify?.shop}/api/${window.theme?.storefrontAPI?.version}/graphql.json`;
    const options = {
      method: 'POST',
      headers: storefrontRequestHeaders,
    };

    try {
      const response = await fetch(url, {
        ...options,
        body: cartQuery(),
      });

      if (response.ok === false) {
        throw new Error('Cart query response was not OK');
      }

      const json = await response.json();
      const cartData = json?.data?.cart;

      if (!cartData) {
        throw new Error('Cart data is not defined');
      }

      const totalPrice = cartData?.cost?.totalAmount?.amount * 100;
      const appliedGiftCards = cartData?.appliedGiftCards;
      const newDiscountData = {
        totalPrice,
        appliedGiftCards,
      };

      setDiscountData(newDiscountData);
    } catch (err) {
      window.console.error(err);
    }
  };

  const fetchCustomCartData = async () => {
    const url = `${rootUrl}/cart?view=custom-data`;
    const options = {
      headers: {
        'Content-Type': 'application/json',
      },
    };
    try {
      const response = await fetch(url, options);
      if (response.ok === false) {
        throw new Error('Fetch cart response was not OK');
      }
      const json = await response.json();
      return json;
    } catch (err) {
      window.console.error(err);
    }
  };

  /**
   *
   * @typedef {Object} Item
   * @property {number} id - The Shopify variant ID.
   * @property {number} quantity - The quantity to add of this variant.
   * @property {Object.<string, string>} [properties] - Optional line item properties.
   * @property {Object} [variant] - Optional variant.
   */

  /**
   *
   * @param {...Item} items - Array of {@link Item} objects to add to cart.
   */
  const addLineItems = async (items, productData) => {
    const data = {
      items: [items].flat(),
    };
    const url = `${rootUrl}/cart/add.js`;
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    };
    try {
      const response = await fetch(url, options);
      if (response.ok === false) {
        const errorResponse = await response.json();
        setErrorMessage(errorResponse.description);
      }
      const cartContents = await fetchCart();
      updateCart(cartContents);

      // Klaviyo tracking for add to cart
      if (window.klaviyo && window.theme.klaviyo?.enableAddToCartTracking) {
        const cartTracking = {
          total_price: cartContents.total_price / 100,
          $value: cartContents.total_price / 100,
          total_discount: cartContents.total_discount,
          original_total_price: cartContents.original_total_price / 100,
          items: cartContents.items,
        };

        let productTracking = {};

        if (typeof window.klProduct !== 'undefined') {
          productTracking = window.klProduct;
          if (productTracking.ImageURL?.startsWith('//')) {
            productTracking.ImageURL = `https:${productTracking.ImageURL}`;
          }
        }

        window.klaviyo.push([
          'track',
          'Added to Cart',
          {
            ...productTracking,
            cart: cartTracking,
          },
        ]);
      }
    } catch (err) {
      window.console.error(err);
    }
  };

  const updateLineItems = async (updates = {}, attributes, callback) => {
    const additionalItemsToRemove = [];
    const data = {
      updates,
    };

    if (window.theme.gwp?.enabled) {
      additionalItemsToRemove.push(...getInvalidGifts({updates}));
    }

    if (additionalItemsToRemove.length) {
      additionalItemsToRemove.forEach((item) => {
        data.updates = {...data.updates, ...item};
      });
    }

    if (attributes) {
      data.attributes = attributes;
    }

    const url = `${rootUrl}/cart/update.js`;
    const options = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    };
    try {
      const response = await fetch(url, options);
      if (response.ok === false) {
        throw new Error('cart/update response was not OK');
      }
      const cartContents = await response.json();
      updateCart(cartContents);
      if (callback) {
        callback();
      }

      // Dispatch custom event (used for tracking)
      document.dispatchEvent(new CustomEvent('sidecart:update-items', {detail: cartContents}));
    } catch (err) {
      window.console.error(err);
    }
  };

  const handleCartToggle = (event) => {
    document.body.style.removeProperty('overflow');

    if (event) {
      event.preventDefault();
    }

    const closeSearchOnCartDrawer = new Event('closeSearch');
    document.dispatchEvent(closeSearchOnCartDrawer);

    if (document.body.classList.contains('cart-is-visible')) {
      document.body.classList.remove('cart-is-visible');
      const cartOpenButton = document.querySelector('.header__btn--cart');
      if (cartOpenButton) {
        cartOpenButton.focus();
      }
    } else {
      document.body.classList.add('cart-is-visible');
      const cartCloseButton = document.querySelector('.side-cart__close');
      if (cartCloseButton) {
        cartCloseButton.focus();
      }

      // Track cartView event
      if (window.theme.enableLexer) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({event: 'cartView'});
      }
    }
  };

  /**
   *
   * @param {...Item} items - Array of {@link Item} objects to add to cart.
   * @param {boolean} openCart - Triggers the cart to open when item added if true
   */

  const handleAddToCartEvent = async (items, openCart = false, productData = {}, callback) => {
    // Items could contain extra data like properties or variant info.
    // This reassignment strips out all but id, qty and properties to pass
    // to the addLineItems function for the cart.

    const lineItems = [items].flat().map((item) => {
      // ID and Qty will always be part of the array
      const newLineItem = {
        id: item.id,
        quantity: item.quantity,
      };
      // Only add properties if they exist
      if ('properties' in item) {
        newLineItem.properties = item.properties;
      }
      return newLineItem;
    });
    try {
      await addLineItems(lineItems, productData);

      if (openCart) {
        handleCartToggle();
      }
      if (callback) {
        callback();
      }

      // Dispatch custom event (used for tracking)
      document.dispatchEvent(new Event('sidecart:add-to-cart'));
    } catch (err) {
      window.console.error(err);
    }
  };

  useEffect(async () => {
    window.sideCart = {
      handleAddToCartEvent,
    };

    const cartToggles = document.querySelectorAll('[data-toggle-cart]');
    cartToggles.forEach((cartToggle) => cartToggle.addEventListener('click', handleCartToggle));

    // Add listener for cart:updated event
    document.addEventListener('cart:updated', async (event) => {
      if (event.detail) {
        updateCart(event.detail);
      }
    });

    // Fetch discount data
    await fetchDiscountData();

    // Retrieve custom cart data from sessionStorage or fetch new custom cart data
    const savedCartCustomData = sessionStorage.getItem('cartCustomData');
    if (savedCartCustomData) {
      setCustomCartData(JSON.parse(savedCartCustomData));
    } else {
      const newCustomCartData = await fetchCustomCartData();
      setCustomCartData(newCustomCartData);
      sessionStorage.setItem('cartCustomData', JSON.stringify(newCustomCartData));
    }

    // Add listener for add to cart event from status customer accounts app and update cart
    document.addEventListener('status:cart:add', async () => {
      const cartContents = await fetchCart();
      updateCart(cartContents);
    });

    // Add listener for add to cart event from wishlist and update cart
    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push(function (swat) {
      swat.evtLayer.addEventListener('sw:addedtocart', async (e) => {
        const cartContents = await fetchCart();
        updateCart(cartContents);
      });
    });
  }, []);

  useEffect(async () => {
    // Update the cart contents
    const cartContents = await fetchCart();
    updateCart(cartContents);

    // Update the custom cart data
    const newCustomCartData = await fetchCustomCartData();
    setCustomCartData(newCustomCartData);
    sessionStorage.setItem('cartCustomData', JSON.stringify(newCustomCartData));
  }, [appliedDiscounts]);

  return (
    <>
      {isCartPage && (
        <>
          <div className="page-header">
            <h1 className="side-cart__title h3">
              <strong>
                {window.theme.strings.cartTitle} ({itemsCount})
              </strong>
            </h1>
          </div>
        </>
      )}
      {!isCartPage && (
        <div className="side-cart__header">
          <div className="side-cart__header-content side-cart__section">
            <div className="side-cart__title-wrapper">
              <h4 className="side-cart__title">
                <strong>
                  {window.theme.strings.cartTitle} ({itemsCount})
                </strong>
              </h4>
            </div>
            <button
              type="button"
              className="side-cart__close btn"
              aria-label={window.theme.strings.closeCart}
              data-toggle-cart
            >
              <span dangerouslySetInnerHTML={{__html: iconClose}} />
            </button>
          </div>
        </div>
      )}
      {isCartPage && cartItems?.length > 0 && (
        <div className="cart-page__table-header">
          <strong className="cart-page__table-header-item text--regular">{window.theme.strings.item}</strong>
          <strong className="cart-page__table-header-qty text--regular">{window.theme.strings.quantity}</strong>
          <strong className="cart-page__table-header-price text--regular">{window.theme.strings.price}</strong>
        </div>
      )}
      {errorMessage && (
        <div className="side-cart__error-message">
          {errorMessage}
          <button
            type="button"
            className="side-cart__dismiss-message btn"
            aria-label={window.theme.strings.dismissErrorMessage}
            onClick={() => setErrorMessage(null)}
          >
            <span dangerouslySetInnerHTML={{__html: iconClose}} />
          </button>
        </div>
      )}
      {gwp && (
        <GiftWithPurchase
          gifts={gwp.gifts}
          excludedGifts={gwp.excludedGifts}
          claimedGifts={gwp.claimedGifts}
          remainingSpend={gwp.remainingSpend}
          addToCart={handleAddToCartEvent}
        />
      )}
      <div className="side-cart__content-container">
        <div className="side-cart__content">
          {cartItems?.length > 0 && (
            <ul className="side-cart__cart-items-container side-cart__section list-none">
              {cartItems.map((item) => {
                const customData = customCartData?.items?.find((customCartItem) => customCartItem.id === item.id) ?? {};
                return (
                  <CartItem
                    key={item.key}
                    item={item}
                    customData={customData}
                    updateLineItems={updateLineItems}
                    isCartPage={isCartPage}
                  />
                );
              })}
            </ul>
          )}
          {cartItems?.length <= 0 && (
            <div className="side-cart__empty side-cart__section">
              <div className="side-cart__empty-ctas">
                {cartEmptyText && (
                  <p>
                    <strong>{cartEmptyText}</strong>
                  </p>
                )}
                {cartyEmptyCTAs.length > 0 && (
                  <ul className="list-none">
                    {cartyEmptyCTAs.map((item) => {
                      return (
                        <li>
                          <a className="side-cart__empty-cta" href={item.url}>
                            {item.text}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              {cartTrustIndicators.length > 0 && (
                <ul className="side-cart__trust-indicators list-none">
                  {cartTrustIndicators.map((item) => {
                    return (
                      <li key={item.key} className="side-cart__trust-indicator">
                        {item.url ? (
                          <a href={item.url}>
                            {item.icon && <img src={item.icon} alt={item.text} width="24" height="24" />}
                            <strong>{item.text}</strong>
                          </a>
                        ) : (
                          <>
                            {item.icon && <img src={item.icon} alt={item.text} width="24" height="24" />}
                            <strong>{item.text}</strong>
                          </>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>

        {cartItems?.length > 0 && window.theme.deliveryTimer.enabled && window.theme.deliveryTimer.enabledInCart && (
          <delivery-timer className="delivery-timer delivery-timer--cart" />
        )}

        {window.theme?.customer?.loggedIn && window.theme?.cartSettings?.enableStatusBlock ? (
          <div className="side-cart__status-block">
            <status-collection
              className="status-shopping-block"
              type="recentlyViewed"
              heading="Recently Viewed"
              heading-alignment="left"
              show-carousel="true"
              product-amount="8"
              customer-button=""
              show-product-titles="true"
              hide-if-empty="true"
              custom-css=".collection-heading{font-size: 14px; font-weight: 700; padding: 0 16px;} .collection-more-button{display:none} .product-title, .collection-items .product-thumbnail .product-price {font-size:12px; line-height: 1.333; font-weight: 700} .collection-items{padding: 0 16px; scroll-padding: 0 16px;} .collection-items .product-thumbnail .base-image{border-radius:0} .collection-items .product-thumbnail .base-image:before{border:none} @media screen and (min-width: 480px) {.collection-heading{padding: 0 58px;} .collection-items{padding: 0 58px; scroll-padding: 0 58px;}}"
              style="--side-padding:0; max-width:362px;"
            ></status-collection>
          </div>
        ) : (
          enableTagalysCartWidget &&
          widgetProductIds.length > 0 && (
            <CartTagalysRecommendations widgetProductIds={widgetProductIds} isCartPage={isCartPage} />
          )
        )}

        {window.theme?.cartBanner?.display && cartItems?.length > 0 && <PromoBanner />}

        {cartItems?.length > 0 && (
          <div className="side-cart__footer">
            {freeShippingBarSettings.enabled && (
              <FreeShippingBar total={total} freeShippingBarSettings={freeShippingBarSettings} />
            )}
            <Discount appliedDiscounts={appliedDiscounts} setAppliedDiscounts={setAppliedDiscounts} />
            <div className="side-cart__footer-content side-cart__section">
              <>
                <div className="side-cart__totals">
                  {window.theme?.cartSettings?.displayUrgencyMessage && (
                    <p
                      className="side-cart__footer-message"
                      dangerouslySetInnerHTML={{__html: window.theme?.strings?.cartFooter?.urgencyMessage}}
                    />
                  )}

                  {lineItemDiscountsGrouped.length > 0 &&
                    lineItemDiscountsGrouped.map((discount) => (
                      <div className="side-cart__savings">
                        <strong>{window.theme.strings.discount}</strong>
                        <strong className="side-cart__discount-title">{discount.title}</strong>
                        <strong
                          dangerouslySetInnerHTML={{__html: formatMoney(discount.amount, window.theme.moneyFormat)}}
                        />
                      </div>
                    ))}

                  {discountData?.appliedGiftCards?.length > 0 &&
                    discountData.appliedGiftCards.map((giftCard) => (
                      <div className="side-cart__savings">
                        <strong>{window.theme.strings.gift_card}</strong>
                        <strong className="side-cart__discount-title">•••• {giftCard.lastCharacters}</strong>
                        <strong
                          dangerouslySetInnerHTML={{
                            __html: formatMoney(giftCard.amountUsed.amount * 100, window.theme.moneyFormat),
                          }}
                        />
                      </div>
                    ))}

                  {lineItemDiscountsGrouped.length <= 0 && cart.total_discount > 0 && (
                    <div className="side-cart__savings">
                      <strong>{window.theme.strings.discount}</strong>
                      <strong
                        dangerouslySetInnerHTML={{__html: formatMoney(cart.total_discount, window.theme.moneyFormat)}}
                      />
                    </div>
                  )}
                  <div className="side-cart__subtotal">
                    <strong>{window.theme.strings.subtotal}</strong>
                    <strong dangerouslySetInnerHTML={{__html: formatMoney(total, window.theme.moneyFormat)}} />
                  </div>

                  <div className="side-cart__action-btns">
                    <a href={`${rootUrl}/checkout`} className="side-cart__checkout-btn">
                      {window.theme.strings.checkout}
                    </a>
                  </div>
                  {isCartPage && (
                    <a className="side-cart__continue-shopping" href="/">
                      {window.theme.strings.continueShopping}
                    </a>
                  )}
                  {window.theme?.cartSettings?.displayCheckoutMessage && (
                    <p
                      className="side-cart__footer-message"
                      dangerouslySetInnerHTML={{__html: window.theme?.strings?.cartFooter?.checkoutMessage}}
                    />
                  )}
                </div>
              </>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

SideCart.propTypes = {
  rootUrl: PropTypes.string,
  freeShippingBarSettings: PropTypes.func,
};

export default SideCart;
